import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, ArrowLeft, AlertCircle, Instagram } from 'lucide-react';
import DOMPurify from 'dompurify';
import api from '../api';
import Seo from '../components/Seo';
import './Blog.css';

const dataIt = (d) => (d ? new Date(d).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' }) : '');

// Logo WhatsApp ufficiale (lucide non ha le icone brand): SVG inline.
const WhatsAppIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.945C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 0 1 8.413 3.488 11.824 11.824 0 0 1 3.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.247-.694.247-1.289.173-1.413z" />
  </svg>
);

function Articolo() {
  const { slug } = useParams();
  const [articolo, setArticolo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const carica = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/blog/${slug}`);
        setArticolo(res.data);
      } catch (_e) {
        setError('Articolo non trovato');
      } finally {
        setLoading(false);
      }
    };
    carica();
  }, [slug]);

  if (loading) {
    return <div className="blog-page"><div className="blog-loading"><div className="spinner" /> Caricamento...</div></div>;
  }

  if (error || !articolo) {
    return (
      <div className="blog-page">
        <div className="blog-empty">
          <AlertCircle size={40} />
          <h2>{error || 'Articolo non trovato'}</h2>
          <Link to="/blog" className="btn btn-primary">Torna al blog</Link>
        </div>
      </div>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: articolo.titolo,
    ...(articolo.estratto ? { description: articolo.estratto } : {}),
    ...(articolo.copertina_url ? { image: articolo.copertina_url } : {}),
    ...(articolo.autore ? { author: { '@type': 'Person', name: articolo.autore } } : {}),
    ...(articolo.pubblicato_at ? { datePublished: articolo.pubblicato_at } : {}),
    ...(articolo.updated_at ? { dateModified: articolo.updated_at } : {}),
  };

  // L'articolo è HTML (editor ricco). Lo sanifichiamo prima del render → no XSS.
  // Fallback: se è vecchio testo semplice (niente tag), preserva gli a-capo.
  const grezzo = String(articolo.contenuto || '');
  const sembraHtml = /<\/?[a-z][\s\S]*>/i.test(grezzo);
  const htmlSicuro = DOMPurify.sanitize(
    sembraHtml ? grezzo : grezzo.split(/\n{2,}/).map((p) => `<p>${p.replace(/\n/g, '<br>')}</p>`).join(''),
    { ADD_ATTR: ['target', 'rel'] }
  );

  return (
    <article className="blog-page articolo-page">
      <Seo
        title={`${articolo.titolo} — Blog Feste Da Piccoli`}
        description={(articolo.estratto || articolo.titolo).slice(0, 155)}
        image={articolo.copertina_url}
        type="article"
        canonical={`/blog/${articolo.slug}`}
        jsonLd={jsonLd}
      />

      <div className="articolo-back">
        <Link to="/blog"><ArrowLeft size={16} /> Tutti gli articoli</Link>
      </div>

      <h1 className="articolo-titolo">{articolo.titolo}</h1>
      <div className="articolo-meta">
        {articolo.autore && <span>di {articolo.autore}</span>}
        {articolo.pubblicato_at && <span><Calendar size={14} /> {dataIt(articolo.pubblicato_at)}</span>}
      </div>

      <div className="articolo-share">
        <a
          className="share-whatsapp"
          href={`https://wa.me/?text=${encodeURIComponent(`${articolo.titolo}\n${typeof window !== 'undefined' ? window.location.href : ''}`)}`}
          target="_blank"
          rel="noreferrer"
          aria-label="Condividi su WhatsApp"
        >
          <WhatsAppIcon size={18} /> Condividi su WhatsApp
        </a>
        <a
          className="follow-instagram"
          href="https://www.instagram.com/festedapiccoli/"
          target="_blank"
          rel="noreferrer"
          aria-label="Seguici su Instagram"
        >
          <Instagram size={18} /> Seguici su Instagram
        </a>
      </div>

      {articolo.copertina_url && (
        <div className="articolo-cover">
          <img src={articolo.copertina_url} alt={articolo.titolo} />
        </div>
      )}

      <div className="articolo-contenuto" dangerouslySetInnerHTML={{ __html: htmlSicuro }} />
    </article>
  );
}

export default Articolo;
